/**
 * Skeleton-fed outline rows — a faithful, bug-for-bug port of the parity
 * oracle's candidate side (`tests/test_helpers/outline/candidate.py` in the
 * inspect_ai repo). That module derives transcript-outline rows from a
 * `SampleSkeleton` alone and is pinned against the frozen legacy pipeline by
 * a parity suite; divergence classes from the legacy outline are signed off
 * there (see its docstring for the transform-equivalents and preserved
 * quirks). Fix behavior in the Python candidate first, then mirror it here.
 */
import { at } from "./format";
import type { SampleSkeleton, SkeletonNotable, SkeletonSpan } from "./types";

/** `kSandboxSignalName` (inspect-components `transform/fixups.ts`). */
const SANDBOX_SIGNAL_NAME = "53787D8A-D3FC-426D-B383-9F880B70E4AA";

/** One outline row (the Python `OutlineRow` NamedTuple, plus `spanId`). */
export interface OutlineRow {
  kind: "span" | "turns" | "scoring" | "event";
  depth: number;
  name?: string;
  type?: string;
  /**
   * Sequence index of the row's anchor event (absent only for synthetic
   * rows whose subtree holds no indexed events).
   */
  anchor?: number;
  /** Merged item count (turns / scoring rows). */
  total?: number;
  /**
   * The skeleton span's id (span rows only) — keys collapse state. Absent
   * on the synthetic sample_init wrapper row, which has no skeleton span.
   */
  spanId?: string;
}

/**
 * Collapse state: a preset, or a set of span identities (span ids; legacy
 * steps use the skeleton convention `step-<begin index>`).
 */
export type CollapseState =
  "default" | "expanded" | "collapsed" | ReadonlySet<string>;

interface CandidateSpan {
  /**
   * Undefined for the virtual root (which has no gaps: root-level loose
   * model events are not counted anywhere in the skeleton).
   */
  span: SkeletonSpan | undefined;
  items: Item[];
  gaps: number[];
}

type Item = CandidateSpan | SkeletonNotable;

const isSpanNode = (item: Item): item is CandidateSpan => "items" in item;

const spanOf = (node: CandidateSpan): SkeletonSpan => {
  if (node.span === undefined) {
    throw new Error("virtual root has no span");
  }
  return node.span;
};

const itemPosition = (item: Item): number =>
  isSpanNode(item) ? spanOf(item).begin : item.i;

const buildTree = (skeleton: SampleSkeleton): CandidateSpan => {
  const nodes: CandidateSpan[] = skeleton.spans.map((span) => ({
    span,
    items: [],
    gaps: [...span.gap_models],
  }));

  const itemsOf = (parent: number | undefined): Item[] =>
    [
      ...nodes.filter(
        (node) => node.span !== undefined && node.span.parent === parent
      ),
      ...skeleton.notables.filter((n) => n.span === parent),
    ].sort((a, b) => itemPosition(a) - itemPosition(b));

  nodes.forEach((node, index) => {
    node.items = itemsOf(index);
  });
  const rootItems = itemsOf(undefined);
  return {
    span: undefined,
    items: rootItems,
    gaps: new Array<number>(rootItems.length + 1).fill(0),
  };
};

/** Splice a child span's items and gaps into its parent additively. */
const dissolveItem = (parent: CandidateSpan, index: number): void => {
  const child = at(parent.items, index);
  if (!isSpanNode(child) || child.span === undefined) {
    throw new Error("dissolve target must be a structural span");
  }
  const gaps = parent.gaps;
  const merged =
    child.gaps.length === 1
      ? // a childless span's single gap merges into both adjacent gaps
        [
          ...gaps.slice(0, index),
          at(gaps, index) + at(child.gaps, 0) + at(gaps, index + 1),
          ...gaps.slice(index + 2),
        ]
      : [
          ...gaps.slice(0, index),
          at(gaps, index) + at(child.gaps, 0),
          ...child.gaps.slice(1, -1),
          at(child.gaps, child.gaps.length - 1) + at(gaps, index + 1),
          ...gaps.slice(index + 2),
        ];
  parent.items.splice(index, 1, ...child.items);
  parent.gaps = merged;
};

const DISSOLVED_TYPES: ReadonlySet<string> = new Set([
  "main",
  "solvers",
  "checkpoint",
]);

const unqualified = (name: string): string => {
  const sep = name.indexOf("/");
  const tail = sep === -1 ? "" : name.slice(sep + 1);
  return tail || name;
};

/** Python-side dict equality: `{"state": 1}` or `{"state": 1, "store": 1}`. */
const isStateOnlyChildren = (children: Record<string, number>): boolean => {
  const keys = Object.keys(children);
  return (
    children.state === 1 &&
    (keys.length === 1 || (keys.length === 2 && children.store === 1))
  );
};

/** `unwrap_agent_solver`: solver span == [agent span] + state (+ store). */
const isAgentSolverWrapper = (node: CandidateSpan): boolean => {
  const span = node.span;
  if (span === undefined || span.type !== "solver") {
    return false;
  }
  const childSpans = node.items.filter(isSpanNode);
  if (childSpans.length !== node.items.length || childSpans.length !== 1) {
    return false;
  }
  const child = at(childSpans, 0);
  return (
    child.span !== undefined &&
    child.span.type === "agent" &&
    isStateOnlyChildren(span.children) &&
    node.gaps.reduce((a, b) => a + b, 0) === 0
  );
};

const isAgentOrSolver = (type: string | undefined): boolean =>
  type === "solver" || type === "agent";

const isSameNameChild = (
  parent: CandidateSpan,
  child: CandidateSpan
): boolean =>
  parent.span !== undefined &&
  child.span !== undefined &&
  isAgentOrSolver(parent.span.type) &&
  isAgentOrSolver(child.span.type) &&
  unqualified(parent.span.name) === unqualified(child.span.name);

const transform = (node: CandidateSpan): void => {
  for (const item of node.items) {
    if (isSpanNode(item)) {
      transform(item);
    }
  }

  if (isAgentSolverWrapper(node)) {
    dissolveItem(node, node.items.findIndex(isSpanNode));
  }

  let index = 0;
  while (index < node.items.length) {
    const item = at(node.items, index);
    if (
      isSpanNode(item) &&
      item.span !== undefined &&
      ((item.span.type !== undefined && DISSOLVED_TYPES.has(item.span.type)) ||
        isSameNameChild(node, item))
    ) {
      dissolveItem(node, index);
    } else {
      index += 1;
    }
  }
};

const isEmptySpan = (node: CandidateSpan): boolean => {
  const span = spanOf(node);
  if (span.type === "fork_nav" || span.type === "empty_branch") {
    return false;
  }
  return (
    Object.keys(span.children).length === 0 &&
    !node.items.some((item) => !isSpanNode(item)) &&
    node.items.every((item) => !isSpanNode(item) || isEmptySpan(item))
  );
};

/**
 * Mirror the legacy `collapseSampleInit` fixup from skeleton facts.
 *
 * Legacy synthesizes a `sample_init` step around the sample_init event on
 * step-only logs with no `init` step. Skeleton equivalents: every span is a
 * folded step pair (`step-<n>` ids), none is named `init`, no span holds
 * the sample_init event, and event 0 is loose (no span begins there) — a
 * sample's first event is invariably its sample_init.
 */
const needsSampleInitRow = (skeleton: SampleSkeleton): boolean => {
  const spans = skeleton.spans;
  return (
    skeleton.counts.events > 0 &&
    spans.every((span) => span.id.startsWith("step-")) &&
    !spans.some((span) => span.name === "init") &&
    !spans.some((span) => (span.children.sample_init ?? 0) > 0) &&
    !spans.some((span) => span.begin === 0)
  );
};

const defaultCollapsed = (span: SkeletonSpan): boolean => {
  if (span.type === "solver" && span.name === "system_message") {
    return true;
  }
  if (
    span.name === SANDBOX_SIGNAL_NAME ||
    span.name === "init" ||
    span.name === "sample_init"
  ) {
    return true;
  }
  // legacy default-collapses the *elevated* tool/subtask event, which
  // exists only when the span holds a direct tool/subtask event child; a
  // tool span without one keeps its (never default-collapsed) span node
  return (
    (span.type === "tool" || span.type === "subtask") &&
    Boolean(span.children[span.type])
  );
};

/**
 * Span ids the default-collapse policy collapses — for seeding transcript
 * collapse state to match `candidateOutlineRows(skeleton, "default")`.
 */
export const defaultCollapsedSpanIds = (
  skeleton: SampleSkeleton
): Set<string> =>
  new Set(skeleton.spans.filter(defaultCollapsed).map((span) => span.id));

/** Outline rows derived from the skeleton alone. */
export const candidateOutlineRows = (
  skeleton: SampleSkeleton,
  collapse: CollapseState = "default"
): OutlineRow[] => {
  const root = buildTree(skeleton);
  transform(root);

  const isCollapsed = (span: SkeletonSpan): boolean => {
    if (collapse === "default") {
      return defaultCollapsed(span);
    }
    if (collapse === "expanded") {
      return false;
    }
    if (collapse === "collapsed") {
      return true;
    }
    return collapse.has(span.id);
  };

  const rows: OutlineRow[] = [];

  const emitSampleInitWrapper = (): void => {
    rows.push({ kind: "span", depth: 0, name: "sample_init", anchor: 0 });
    // the legacy synthetic step has no uuid and no event index, so it has
    // no span identity (hence no spanId) — only the default-collapse policy
    // (name match) ever collapses it
    if (collapse !== "default") {
      rows.push({ kind: "event", depth: 1, type: "sample_init", anchor: 0 });
    }
  };

  const emitGap = (
    node: CandidateSpan,
    k: number,
    anchor: number,
    depth: number
  ): void => {
    const count = k < node.gaps.length ? at(node.gaps, k) : 0;
    if (count > 0) {
      rows.push({ kind: "turns", depth, anchor, total: count });
    }
  };

  const emitSpan = (
    node: CandidateSpan,
    depth: number,
    inScorers: boolean
  ): void => {
    const span = spanOf(node);
    if (isEmptySpan(node)) {
      return;
    }
    rows.push({
      kind: "span",
      depth,
      name: span.name,
      ...(span.type !== undefined ? { type: span.type } : {}),
      anchor: span.begin,
      spanId: span.id,
    });
    if (isCollapsed(span)) {
      return;
    }
    if (inScorers && span.type === "scorer") {
      return;
    }
    // the sample_init event gets a legacy outline row of its own; it is by
    // construction the first event inside its span, so the skeleton's
    // children counter locates it at begin+1
    if ((span.children.sample_init ?? 0) > 0) {
      rows.push({
        kind: "event",
        depth: depth + 1,
        type: "sample_init",
        anchor: span.begin + 1,
      });
    }
    emitItems(node, depth + 1, inScorers || span.type === "scorers");
  };

  const emitItems = (
    node: CandidateSpan,
    depth: number,
    inScorers: boolean
  ): void => {
    const anchor = node.span !== undefined ? node.span.begin + 1 : 0;
    emitGap(node, 0, anchor, depth);
    const scores: SkeletonNotable[] = [];

    const flushScores = (): void => {
      if (scores.length > 0) {
        rows.push({
          kind: "scoring",
          depth,
          anchor: at(scores, 0).i,
          total: scores.length,
        });
        scores.length = 0;
      }
    };

    node.items.forEach((item, k) => {
      let after: number;
      if (!isSpanNode(item)) {
        if (item.type === "score") {
          scores.push(item);
        } else {
          flushScores();
          rows.push({ kind: "event", depth, type: item.type, anchor: item.i });
        }
        after = item.i + 1;
      } else {
        flushScores();
        emitSpan(item, depth, inScorers);
        after = spanOf(item).extent[1] + 1;
      }
      // a nonzero gap emits a turns row, which breaks a run of adjacent
      // score rows — flush before it (a zero gap emits nothing, so buffered
      // scores keep merging across it)
      if ((k + 1 < node.gaps.length ? at(node.gaps, k + 1) : 0) !== 0) {
        flushScores();
      }
      emitGap(node, k + 1, after, depth);
    });
    flushScores();
  };

  if (needsSampleInitRow(skeleton)) {
    emitSampleInitWrapper();
  }
  emitItems(root, 0, false);
  return rows;
};
